import React from 'react';
import { Divider, List, ListSubheader } from '@mui/material';
import { styled } from '@mui/material/styles';

import { isNavItemActive } from '@/config/navigation';

import { useNavSection } from '../hooks/useNavSection';

import NavigationItem, { type NavItemData } from './NavigationItem';

const StyledSubheader = styled(ListSubheader as React.ComponentType<any>)(
  ({ theme }) => ({
    backgroundColor: 'transparent',
    lineHeight: 1.4,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    '&:first-of-type': {
      paddingTop: theme.spacing(1),
    },
  })
);

interface NavGroup {
  groupId: string;
  groupLabel: string;
  items: NavItemData[];
}

interface NavigationListProps {
  items?: NavItemData[];
  groups?: NavGroup[];
  currentPath?: string;
  /** Current query string: a shared record's origin decides which item lights. */
  currentSearch?: string;
  onItemClick?: (item: NavItemData) => void;
  showAsLinks?: boolean;
  /** Icon-rail mode: icons only; group labels compress to dividers. */
  rail?: boolean;
}

const NavigationList = ({
  items,
  groups,
  currentPath,
  currentSearch,
  onItemClick,
  showAsLinks = true,
  rail = false,
}: NavigationListProps) => {
  const section = useNavSection(currentPath, currentSearch);
  const renderItemList = (listItems: NavItemData[], keyPrefix = '') =>
    listItems.map(item => (
      <NavigationItem
        key={keyPrefix + item.id}
        item={item}
        isSelected={isNavItemActive(currentPath, item.path, section)}
        onClick={() => onItemClick && onItemClick(item)}
        showAsLink={showAsLinks}
        rail={rail}
      />
    ));

  if (groups && groups.length > 0) {
    return (
      <List>
        {groups.map(({ groupId, groupLabel, items: groupItems }, index) => (
          <React.Fragment key={groupId}>
            {rail ? (
              index > 0 && <Divider component="li" sx={{ my: 1, mx: 2 }} />
            ) : (
              <StyledSubheader component="li" disableSticky>
                {groupLabel}
              </StyledSubheader>
            )}
            {renderItemList(groupItems, `${groupId}-`)}
          </React.Fragment>
        ))}
      </List>
    );
  }

  if (!items || items.length === 0) {
    return null;
  }

  return <List>{renderItemList(items)}</List>;
};

export default NavigationList;
